import React, { useCallback, useEffect, useMemo, useState } from 'react'
import GrantSummary from '../components/GrantSummary'
import * as echarts from 'echarts/core'
import clsx from 'clsx'
import ClipLoader from 'react-spinners/ClipLoader'
import {
  LineChart,
  BarChart,
  PieChart,
  // ScatterChart,
  // RadarChart,
  // MapChart,
  // TreeChart,
  // TreemapChart,
  // GraphChart,
  GaugeChart,
  // FunnelChart,
  // ParallelChart,
  // SankeyChart,
  // BoxplotChart,
  // CandlestickChart,
  // EffectScatterChart,
  // LinesChart,
  // HeatmapChart,
  // PictorialBarChart,
  // ThemeRiverChart,
  // SunburstChart,
  // CustomChart,
} from 'echarts/charts'
// import components, all suffixed with Component
import {
  // GridSimpleComponent,
  GridComponent,
  // PolarComponent,
  // RadarComponent,
  // GeoComponent,
  // SingleAxisComponent,
  // ParallelComponent,
  // CalendarComponent,
  // GraphicComponent,
  ToolboxComponent,
  TooltipComponent,
  // AxisPointerComponent,
  // BrushComponent,
  TitleComponent,
  // TimelineComponent,
  // MarkPointComponent,
  // MarkLineComponent,
  // MarkAreaComponent,
  LegendComponent,
  // LegendScrollComponent,
  // LegendPlainComponent,
  // DataZoomComponent,
  // DataZoomInsideComponent,
  // DataZoomSliderComponent,
  // VisualMapComponent,
  // VisualMapContinuousComponent,
  // VisualMapPiecewiseComponent,
  // AriaComponent,
  // TransformComponent,
  // DatasetComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import PrivateLayout from '../components/PrivateLayout'
import Footer from '../components/Footer'
import { DashboardData, OrganizationData, UserData } from '../types'
import { connect } from 'react-redux'
import { useLazyGetStatsQuery } from '../store/api'
import { formatNumericValue } from '../utils'
import WorksSummary from '../components/WorksSummary'
import TopGrantees from '../components/TopGrantees'
import DashBoardLineChart from '../components/DashBoardLineChart'

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  ToolboxComponent,
  BarChart,
  LineChart,
  PieChart,
  GaugeChart,
  CanvasRenderer,
])

interface DashBoardStateProps {
  user: UserData
  organization: OrganizationData
  stats: DashboardData
}

type DashBoardProps = DashBoardStateProps

const DashBoard = ({ organization, stats, user }: DashBoardProps): JSX.Element => {
  const isFunder = useMemo(() => {
    return user && user.roles.includes('ROLE_FUNDER')
  }, [user])

  const [activeGrantStatTab, setActiveGrantStatTab] = useState<'grantAmount' | 'grantMoneyAmount'>('grantAmount')

  const [getStats] = useLazyGetStatsQuery()

  const loadStats = useCallback(async () => {
    setIsApiRequestInProgress(true)

    try {
      await getStats().unwrap()
      setIsApiRequestInProgress(false)
    } catch (e) {
      console.error(e)
      setIsApiRequestInProgress(false)
    }
  }, [getStats])

  useEffect(() => {
    loadStats()
  }, [])

  const [isApiRequestInProgress, setIsApiRequestInProgress] = useState<boolean>(false)

  return (
    <PrivateLayout>
      <div className="content">
        <div className="pb-5">
          <div className="row g-4">
            <div className="col-12 col-xxl-6">
              <div className="mb-8">
                <h3 className="mb-2">{organization?.name}</h3>
                <h5 className="text-700 fw-semi-bold">A snapshot of your foundation</h5>
              </div>
              {isApiRequestInProgress ? (
                <div className="spinner-wrapper mb-5">
                  <ClipLoader color="#e5780b" loading={isApiRequestInProgress} size={35} />
                </div>
              ) : (
                <div className="row align-items-center g-4">
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-center">
                      <span className="fa-stack" style={{ minHeight: '46px', minWidth: '46px' }}>
                        <span
                          className="fa-solid fa-circle fa-stack-2x stack-circle text-success-100"
                          data-fa-transform="up-4 right-3 grow-2"
                        ></span>
                        <span
                          className="fa-stack-1x fa-solid fa-award text-success "
                          data-fa-transform="shrink-2 up-8 right-6"
                        ></span>
                      </span>
                      <div className="ms-3">
                        <h4 className="mb-0">{formatNumericValue(stats.grantsAwarded)}</h4>
                        <p className="text-800 fs--1 mb-0">grants awarded</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-center">
                      <span className="fa-stack" style={{ minHeight: '46px', minWidth: '46px' }}>
                        <span
                          className="fa-solid fa-circle fa-stack-2x stack-circle text-success-100"
                          data-fa-transform="up-4 right-3 grow-2"
                        ></span>
                        <span
                          className="fa-stack-1x fa-solid fa-donate text-success "
                          data-fa-transform="shrink-2 up-8 right-6"
                        ></span>
                      </span>
                      <div className="ms-3">
                        <h4 className="mb-0">{formatNumericValue(stats.grantMoneyAwarded)}</h4>
                        <p className="text-800 fs--1 mb-0">grant money awarded</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-center">
                      <span className="fa-stack" style={{ minHeight: '46px', minWidth: '46px' }}>
                        <span
                          className="fa-solid fa-circle fa-stack-2x stack-circle text-success-100"
                          data-fa-transform="up-4 right-3 grow-2"
                        ></span>
                        <span
                          className="fa-stack-1x fa-solid fa-file-alt text-success "
                          data-fa-transform="shrink-2 up-8 right-6"
                        ></span>
                      </span>
                      <div className="ms-3">
                        <h4 className="mb-0">{formatNumericValue(stats.worksAwarded)}</h4>
                        <p className="text-800 fs--1 mb-0">works created with grant money</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="d-flex align-items-center">
                      <span className="fa-stack" style={{ minHeight: '46px', minWidth: '46px' }}>
                        <span
                          className="fa-solid fa-circle fa-stack-2x stack-circle text-success-100"
                          data-fa-transform="up-4 right-3 grow-2"
                        ></span>
                        <span
                          className="fa-stack-1x fa-solid fa-user text-success "
                          data-fa-transform="shrink-2 up-8 right-6"
                        ></span>
                      </span>
                      <div className="ms-3">
                        <h4 className="mb-0">{formatNumericValue(stats.grantees)}</h4>
                        <p className="text-800 fs--1 mb-0">grantees</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <hr className="bg-200 mb-4 mt-2" />
              <div className="row flex-between-center mb-4 g-3 mt-3">
                <div className="col-12">
                  <h3>Year by year grant statistics</h3>
                  <div className="nav-tabs-wrapper dashboard row mb-4 mt-5">
                    <div className="nopadding">
                      <ul className="nav nav-tabs px-4">
                        <li className="nav-item px-3">
                          <a
                            className={clsx('nav-link', { active: activeGrantStatTab === 'grantAmount' })}
                            onClick={() => {
                              setActiveGrantStatTab('grantAmount')
                            }}
                          >
                            Grants awarded
                          </a>
                        </li>
                        <li className="nav-item px-3">
                          <a
                            className={clsx('nav-link', { active: activeGrantStatTab === 'grantMoneyAmount' })}
                            onClick={() => {
                              setActiveGrantStatTab('grantMoneyAmount')
                            }}
                          >
                            Grant money awarded
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <DashBoardLineChart
                mode={activeGrantStatTab}
                stats={stats}
                isApiRequestInProgress={isApiRequestInProgress}
              />
            </div>

            <div className="col-12 col-xxl-6">
              <div className="row g-3">
                <GrantSummary stats={stats} isApiRequestInProgress={isApiRequestInProgress} />

                <WorksSummary stats={stats} isApiRequestInProgress={isApiRequestInProgress} />
              </div>
            </div>
          </div>
        </div>

        {isFunder ? (
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white pt-7 border-y border-300">
            <TopGrantees stats={stats} isApiRequestInProgress={isApiRequestInProgress} />
          </div>
        ) : null}

        <Footer />
      </div>
    </PrivateLayout>
  )
}

const mapStateToProps = (state: any): DashBoardStateProps => {
  return {
    user: state.auth.user,
    organization: state.auth.organization,
    stats: state.stats.stats,
  }
}

export default connect(mapStateToProps)(DashBoard)
